import { useForm, type UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useRef } from "react";
import { useCodeSuggestion } from "@/hooks/use-code-suggestion";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Map, APIProvider, useMap } from "@vis.gl/react-google-maps";
import { MapIcon, Globe } from "lucide-react";
import DrawingExample from "./drawing-example";
import {
  type CreateServiceAreaFormData,
  createServiceAreaSchema,
  useCreateServiceArea,
} from "@/api/service-areas";
import {
  useGeographicBoundaries,
  useFetchFromOSM,
} from "@/api/geographic-boundaries";
import { toast } from "sonner";

interface CenterPolygonButtonProps {
  boundaryPolygon?: {
    type: string;
    coordinates: number[][][] | number[][][][];
  };
  className?: string;
}

export function CenterPolygonButton({
  boundaryPolygon,
  className = "absolute bottom-4 left-4",
}: CenterPolygonButtonProps) {
  const map = useMap();

  const centerOnPolygon = () => {
    if (boundaryPolygon?.coordinates && map) {
      const bounds = new google.maps.LatLngBounds();

      if (boundaryPolygon.type === "MultiPolygon") {
        // Handle MultiPolygon - iterate through all polygons
        (boundaryPolygon.coordinates as number[][][][]).forEach(
          (polygon: number[][][]) => {
            polygon.forEach((ring: number[][]) => {
              ring.forEach((coord: number[]) => {
                bounds.extend({ lat: coord[1], lng: coord[0] });
              });
            });
          }
        );
      } else {
        // Handle Polygon
        if (boundaryPolygon.coordinates?.[0]?.length > 0) {
          (boundaryPolygon.coordinates as number[][][]).forEach(
            (ring: number[][]) => {
              ring.forEach((coord: number[]) => {
                bounds.extend({ lat: coord[1], lng: coord[0] });
              });
            }
          );
        }
      }

      if (!bounds.isEmpty()) {
        map.fitBounds(bounds);
      }
    }
  };

  if (!boundaryPolygon?.coordinates?.length) {
    return null;
  }

  return (
    <Button
      variant="default"
      onClick={centerOnPolygon}
      className={className}
      size="sm"
    >
      <MapIcon className="h-4 w-4 mr-2" />
      Center Polygon
    </Button>
  );
}

// Create a separate component for the map to use useMap hook
function ServiceAreaMap({
  onPolygonComplete,
  form,
  mode,
}: {
  onPolygonComplete: (polygon: {
    type: "Polygon";
    coordinates: number[][][];
  }) => void;
  form: UseFormReturn<CreateServiceAreaFormData>;
  mode: "create" | "edit";
}) {
  return (
    <>
      <DrawingExample
        onPolygonComplete={onPolygonComplete}
        initialPolygon={
          mode === "edit"
            ? (form.watch("boundary") as {
                type: "Polygon";
                coordinates: number[][][];
              })
            : undefined
        }
      />
      <CenterPolygonButton
        boundaryPolygon={
          form.watch("boundary") as {
            type: string;
            coordinates: number[][][] | number[][][][];
          }
        }
      />
    </>
  );
}

interface CreateServiceAreaFormProps {
  mode?: "create" | "edit";
  defaultValues?: Partial<CreateServiceAreaFormData>;
  onSubmit?: (values: CreateServiceAreaFormData) => void;
  onCancel?: () => void;
  onSuccess?: () => void;
}

export function CreateServiceAreaForm({
  mode = "create",
  defaultValues,
  onSubmit,
  onCancel,
  onSuccess,
}: CreateServiceAreaFormProps) {
  const defaultCenter = { lat: 43.6532, lng: -79.3832 };
  const [selectedGeographicBoundaryId, setSelectedGeographicBoundaryId] =
    useState<string>("");
  const [osmRelationId, setOsmRelationId] = useState<string>("");
  const [showOsmInput, setShowOsmInput] = useState<boolean>(false);
  const userEditedCode = useRef(false);

  const { data: geographicBoundaries, refetch: refetchBoundaries } =
    useGeographicBoundaries();

  const fetchFromOSMMutation = useFetchFromOSM({
    onSuccess: (result) => {
      toast.success(`Successfully fetched boundary: ${result.boundary.name}`);
      void refetchBoundaries();
      // Auto-select the newly created boundary
      setTimeout(() => {
        handleGeographicBoundarySelect(result.boundary.id);
      }, 500);
      setOsmRelationId("");
      setShowOsmInput(false);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to fetch from OpenStreetMap");
    },
  });

  const form = useForm<CreateServiceAreaFormData>({
    resolver: zodResolver(createServiceAreaSchema),
    defaultValues: {
      code: "",
      name: "",
      isActive: true,
      boundary: {
        type: "Polygon" as const,
        coordinates: [[[0, 0]]],
      } as { type: "Polygon"; coordinates: number[][][] },
      metadata: {},
      ...defaultValues,
    },
  });

  // Code suggestion hook - only in create mode
  const { handleNameChange: suggestCodeFromName } = useCodeSuggestion({
    onCodeSuggested: (code) => {
      if (!userEditedCode.current) {
        form.setValue("code", code);
      }
    },
    enabled: mode === "create",
  });

  const createServiceAreaMutation = useCreateServiceArea({
    onSuccess: () => {
      form.reset();
      onSuccess?.();
    },
  });

  const handleSubmit = form.handleSubmit((values) => {
    if (onSubmit) {
      onSubmit(values);
    } else {
      createServiceAreaMutation.mutate(values);
    }
  });

  const handleGeographicBoundarySelect = (geographicBoundaryId: string) => {
    setSelectedGeographicBoundaryId(geographicBoundaryId);
    const geographicBoundary = geographicBoundaries?.find(
      (boundary) => boundary.id === geographicBoundaryId
    );
    if (geographicBoundary) {
      // In edit mode, preserve existing code/name if they already have values
      // Only auto-populate in create mode or when fields are empty
      const currentCode = form.getValues("code");
      const currentName = form.getValues("name");

      if (mode === "create" || !currentCode) {
        form.setValue("code", geographicBoundary.code ?? "");
      }
      if (mode === "create" || !currentName) {
        form.setValue("name", geographicBoundary.name);
      }

      // Handle both Polygon and MultiPolygon boundaries
      const boundary = geographicBoundary.boundary;
      if (boundary.type === "MultiPolygon") {
        // For MultiPolygon, we need to convert to Polygon for the service area
        // Take the largest polygon (most coordinates) from the MultiPolygon
        const coords = boundary.coordinates;
        let largestPolygon = coords[0];
        let maxPoints = largestPolygon.reduce(
          (sum: number, ring: number[][]) => sum + ring.length,
          0
        );

        coords.forEach((polygon: number[][][]) => {
          const pointCount = polygon.reduce(
            (sum: number, ring: number[][]) => sum + ring.length,
            0
          );
          if (pointCount > maxPoints) {
            maxPoints = pointCount;
            largestPolygon = polygon;
          }
        });

        form.setValue("boundary", {
          type: "Polygon",
          coordinates: largestPolygon,
        });

        toast.info(
          "Note: MultiPolygon converted to largest polygon for service area."
        );
      } else {
        form.setValue("boundary", boundary);
      }

      // Note: postcodes are no longer used, keeping metadata for future use
    }
  };

  return (
    <div className="flex flex-col overflow-y-auto max-h-[80vh]">
      <div className="h-[300px] shrink-0 mb-4 relative">
        <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}>
          <Map
            defaultCenter={defaultCenter}
            defaultZoom={13}
            gestureHandling={"greedy"}
            disableDefaultUI={true}
            style={{ width: "100%", height: "100%" }}
          >
            <ServiceAreaMap
              onPolygonComplete={(polygon) => {
                form.setValue("boundary", polygon);
                // Clear the geographic boundary selection when manually drawing
                setSelectedGeographicBoundaryId("");
              }}
              form={form}
              mode={mode}
            />
          </Map>
        </APIProvider>
      </div>

      <div className="flex-1">
        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-6 px-4">
            {/* Geographic Boundary Selection */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-blue-800">
                  Quick Start from Geographic Boundary
                </h4>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowOsmInput(!showOsmInput)}
                  className="text-blue-700 hover:text-blue-800"
                >
                  <Globe className="h-4 w-4 mr-1" />
                  Fetch from OSM
                </Button>
              </div>

              {showOsmInput && (
                <div className="mb-3 p-3 bg-white rounded-md border border-blue-200">
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      placeholder="Enter OSM relation ID (e.g., 2407259)"
                      value={osmRelationId}
                      onChange={(e) => setOsmRelationId(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      size="sm"
                      disabled={
                        !osmRelationId || fetchFromOSMMutation.isPending
                      }
                      onClick={() => {
                        if (osmRelationId) {
                          fetchFromOSMMutation.mutate(osmRelationId);
                        }
                      }}
                    >
                      {fetchFromOSMMutation.isPending ? "Fetching..." : "Fetch"}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setShowOsmInput(false);
                        setOsmRelationId("");
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    Find relation IDs on{" "}
                    <a
                      href="https://www.openstreetmap.org"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      OpenStreetMap
                    </a>
                    . Search for a place and look for "Relation: " in the
                    details.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                {geographicBoundaries && geographicBoundaries.length > 0 ? (
                  <>
                    <Select
                      value={selectedGeographicBoundaryId}
                      onValueChange={(value) => {
                        if (value === "clear") {
                          setSelectedGeographicBoundaryId("");
                          // Reset form to default values
                          form.setValue("name", "");
                          form.setValue("code", "");
                          form.setValue("boundary", {
                            type: "Polygon",
                            coordinates: [[]],
                          } as { type: "Polygon"; coordinates: number[][][] });
                        } else {
                          handleGeographicBoundarySelect(value);
                        }
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a geographic boundary to auto-populate fields" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="clear">
                          <span className="text-gray-500">Clear selection</span>
                        </SelectItem>
                        {geographicBoundaries.map((boundary) => (
                          <SelectItem key={boundary.id} value={boundary.id}>
                            {boundary.name}{" "}
                            {boundary.code ? `(${boundary.code})` : ""}
                            {boundary.source === "osm" && " - OpenStreetMap"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-blue-700">
                      This will auto-populate the name, code, and boundary from
                      the geographic boundary. The boundary will be displayed on
                      the map above.
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-gray-600">
                    No geographic boundaries available. Use "Fetch from OSM"
                    above to add boundaries from OpenStreetMap.
                  </p>
                )}
              </div>
            </div>

            {/* Basic Information Section */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Downtown Service Area"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          suggestCodeFromName(e.target.value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Code</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="downtown_service_area"
                        {...field}
                        onChange={(e) => {
                          field.onChange(e);
                          userEditedCode.current = true;
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-4 rounded-lg border p-4">
                  <div className="flex-1">
                    <FormLabel>Active Status</FormLabel>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Boundary GeoJSON */}
            <FormField
              control={form.control}
              name="boundary"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Boundary (GeoJSON)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder='{"type": "Polygon", "coordinates": [[[lng, lat], ...]]}'
                      className="h-32 font-mono text-sm"
                      {...field}
                      value={JSON.stringify(field.value, null, 2)}
                      onChange={(e) => {
                        try {
                          field.onChange(JSON.parse(e.target.value));
                        } catch {
                          // Handle invalid JSON
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-4">
              {onCancel && (
                <Button type="button" variant="outline" onClick={onCancel}>
                  Cancel
                </Button>
              )}
              {mode === "create" && createServiceAreaMutation.isError && (
                <p role="alert" className="text-sm text-destructive">
                  Unable to create service area. Please try again.
                </p>
              )}
              <Button
                type="submit"
                disabled={
                  mode === "create" && createServiceAreaMutation.isPending
                }
              >
                {mode === "create"
                  ? "Create Service Area"
                  : "Update Service Area"}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
